import { Component, ElementRef, EventEmitter, Inject, OnInit, Output, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AppConfigService } from '@app/app-config.service';
import { AppStore } from '@app/app.store';
import { FilehandlerService } from '@app/core/services/file/filehandler.service';
import { SessionService } from '@app/core/services/session/session.service';
import { SnackbarService } from '@app/core/services/snackbar/snackbar.service';
import { UserService } from '@app/core/services/user/user.service';
import { LoginService } from '@app/features/auth/services/login.service';
import { LoginStateService } from '@app/features/auth/services/login.state.service';
import { FormField } from '@app/shared/models/field.interface';
import { FileUploaded, FILE_REMOVED, FILE_UPDATED } from '@app/shared/models/file.interface';
import { AvatarUploadComponent } from '@components/form-controls/upload/avatar-upload/avatar-upload.component';
import { AvatarUploadModel } from '@components/form-controls/upload/avatar-upload/avatar.interface';
import { ImagesAllowed } from '@components/form-controls/upload/upload/upload.component';
import { TranslateService } from '@ngx-translate/core';
import { throwError } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';

const UD_MAIN = 'MAIN';
const UD_PASSWORD = 'PASSWORD';
const UD_PICTURE = 'PICTURE';

export interface ErrorData {
  invalidOldPassword?: boolean;
  alreadyUsedBefore?: boolean;
}

@Component({
  selector: 'vfs-user-dropdown',
  templateUrl: './user-dropdown.component.html',
  styleUrls: ['./user-dropdown.component.scss']
})
export class UserDropdownComponent implements OnInit {
  @Output() logout = new EventEmitter();
  @Output('closeUserDropdown') closeDropdown = new EventEmitter();
  @ViewChild('inputFile') inputFile: ElementRef;
  @ViewChild('avatarUpload') avatarUpload: AvatarUploadComponent;
  // Change password
  changePasswordForm: FormGroup;
  fieldCurrentPassword: FormField;
  fieldNewPassword: FormField;
  fieldConfirmNewPassword: FormField;
  errorData: ErrorData;
  errorMessage: string;

  page = this.getUserDropdownPages().MAIN;

  newImageUrl = '';
  newImageId = '';
  previousImageId = '';
  image: AvatarUploadModel;
  imageChanged = '';

  constructor(
    protected appStore: AppStore,
    protected translate: TranslateService,
    protected formBuilder: FormBuilder,
    protected appConfigService: AppConfigService,
    protected userService: UserService,
    @Inject('LoginStateService') protected loginStateService: LoginStateService,
    @Inject('LoginService') protected loginService: LoginService,
    protected sessionService: SessionService,
    private snackbarService: SnackbarService,
    private fileHandlerService: FilehandlerService
  ) {
    this.showMain();
    this.fieldCurrentPassword = {
      label: translate.instant('user.dropdown.password.current'),
      required: true,
      formControlName: 'currentPassword'
    };
    this.fieldNewPassword = {
      label: translate.instant('user.dropdown.password.new'),
      required: true,
      formControlName: 'newPassword',
      hint: translate.instant('login.password.inputCharactersHint')
    };
    this.fieldConfirmNewPassword = {
      label: translate.instant('user.dropdown.password.confirm'),
      required: true,
      formControlName: 'confirmNewPassword'
    };
  }

  ngOnInit() {
    this.changePasswordForm = this.formBuilder.group(
      {
        currentPassword: ['', Validators.required],
        newPassword: [
          '',
          [
            Validators.required,
            Validators.pattern(/^(?=.*[0-9])(?=.*[a-z])(?=.*[A-Z])(?=.*[.@#\$%\^&+=])(?=\S+$).{8,}$/)
          ]
        ],
        confirmNewPassword: ['', [Validators.required]]
      },
      {
        validator: this.checkPasswordsMatch
      }
    );
  }

  public getUserDropdownPages() {
    return {
      MAIN: UD_MAIN,
      PASSWORD: UD_PASSWORD,
      PICTURE: UD_PICTURE
    };
  }

  /**
   * Set the status on the main page
   */
  showMain() {
    this.page = this.getUserDropdownPages().MAIN;
  }

  showPasswordForm() {
    this.page = this.getUserDropdownPages().PASSWORD;
  }

  showChangePicture() {
    this.page = this.getUserDropdownPages().PICTURE;
  }

  /**
   * Close the User Dropdown menu, set the status on the main screen and reset the password change form
   */
  close() {
    this.showMain();
    this.resetFormPassword();
    this.closeUserDropdown();
  }

  /**
   * Get the error message depending on the error in the input
   */
  getErrorMessage(element: string) {
    let control = this.changePasswordForm.get(element);
    if (element === 'currentPassword') {
      return control.hasError('required')
        ? 'core.mandatoryField'
        : control.hasError('invalidOldPassword')
        ? 'user.dropdown.password.previousNotCorrect'
        : '';
    } else if (element === 'newPassword') {
      return control.hasError('required')
        ? 'core.mandatoryField'
        : control.hasError('pattern')
        ? 'login.error.invalidFormat'
        : control.hasError('alreadyUsedBefore')
        ? 'user.dropdown.password.used'
        : '';
    } else if (element === 'confirmNewPassword') {
      return control.hasError('required')
        ? 'core.mandatoryField'
        : control.hasError('MatchPassword')
        ? 'login.error.passwordNotMatch'
        : '';
    }
  }

  /**
   * Check if confirmNewPassword matches newPassword
   */
  checkPasswordsMatch(AC: AbstractControl) {
    let password = AC.get('newPassword').value;
    if (AC.get('confirmNewPassword').touched || AC.get('confirmNewPassword').dirty) {
      let verifyPassword = AC.get('confirmNewPassword').value;

      if (password !== verifyPassword && verifyPassword !== '') {
        AC.get('confirmNewPassword').setErrors({ MatchPassword: true });
      } else {
        return null;
      }
    }
  }

  submitChangePassword() {
    this.loginStateService.setError(null);
    this.loginService
      .sendNewPassword(this.changePasswordForm.value.currentPassword, this.changePasswordForm.value.newPassword)
      .subscribe({
        next: _ => {
          this.showMain();
          this.resetFormPassword();
          this.snackbarService.openSnackBar(this.translate.instant('user.dropdown.password.changed'), 'snackbar');
        },
        error: error => {
          this.errorData = error.error.data;
          this.errorMessage = error.error.message;
          if (this.errorData.invalidOldPassword) {
            this.changePasswordForm.get('currentPassword').setErrors({ invalidOldPassword: true });
          } else if (this.errorData.alreadyUsedBefore) {
            this.changePasswordForm.get('newPassword').setErrors({ alreadyUsedBefore: true });
          }
        }
      });
  }

  resetFormPassword() {
    this.changePasswordForm.reset();
  }

  cancelChangesPassword() {
    this.showMain();
    this.resetFormPassword();
  }

  cancelChangePicture() {
    if (this.imageChanged !== '') {
      const session = this.loginStateService.getSession();
      session.user.image = this.previousImageId;
      this.image.preview = null;
      this.loginStateService.setSession(session);
    }
    this.showMain();
  }

  submitPicture() {
    if (this.imageChanged === FILE_UPDATED) {
      this.uploadFile();
    } else if (this.imageChanged === FILE_REMOVED) {
      this.removeUserImage();
    }
    this.imageChanged = '';
  }

  selectImage() {
    const session = this.loginStateService.getSession();
    this.previousImageId = session.user.image;
    this.inputFile.nativeElement.onchange = () => {
      let file = this.inputFile.nativeElement.files[0];
      if (this.fileHandlerService.checkIfImageAllowed(file)) {
        let reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
          this.image = {
            file,
            preview: reader.result
          };
        };
        this.imageChanged = FILE_UPDATED;
      } else {
        this.snackbarService
          .openSnackBar(this.translate.instant('core.files.imagesAllowed'), 'warning', '', true, 5000)
          .subscribe(_ => {});
      }
    };
    this.inputFile.nativeElement.click();
  }

  /**
   * Function to upload file
   * @param file file to uplaod
   */
  public uploadFile() {
    const session = this.loginStateService.getSession();
    const fd = new FormData();
    let imageData: FileUploaded;
    fd.append('file', this.image.file);
    fd.append('module', 'USR');
    this.image.sub = this.fileHandlerService
      .uploadFile(fd)
      .pipe(
        catchError(error => throwError(error)),
        tap(imageResponse => {
          imageData = imageResponse;
        }),
        switchMap(imageResponse => this.userService.changeImage(session.user.id, imageResponse.id)),
        catchError(error => throwError(error))
      )
      .subscribe(_ => {
        session.user.image = imageData.id;
        this.updateUserImage(imageData);
        this.showMain();
      });
  }

  updateUserImage(evt) {
    if (evt.url) {
      const session = this.loginStateService.getSession();
      this.newImageUrl = evt.url;
      this.newImageId = evt.id;
      this.previousImageId = session.user.image;
      session.user.image = evt.id;
      this.loginStateService.setUser(session.user);
      this.loginStateService.setSession(session);
      this.sessionService.set(session);
      this.image.preview = null;
    }
  }

  removeUserImage() {
    const session = this.loginStateService.getSession();
    this.fileHandlerService.deleteUserImage(session.user.id).subscribe(_ => {
      this.showMain();
      this.loginStateService.setUser(session.user);
    });
  }

  removeUserImageTemp() {
    const session = this.loginStateService.getSession();
    this.previousImageId = session.user.image;
    session.user.image = null;
    this.imageChanged = FILE_REMOVED;
  }

  closeUserDropdown() {
    if (this.imageChanged !== '') {
      const session = this.loginStateService.getSession();
      session.user.image = this.previousImageId;
      this.loginStateService.setSession(session);
      this.image.preview = null;
    }
    this.page = this.getUserDropdownPages().MAIN;
    this.closeDropdown.emit();
  }

  getImagesAllowed() {
    return ImagesAllowed;
  }

  get nameAndSurname() {
    let user = this.appStore.state.session.user;
    return `${user.name ? user.name : ''} ${user.surname ? user.surname : ''}`;
  }

  isSSO() : boolean {
    return this.appConfigService.isAdfsActive();
  }
}
